import { useCallback, useEffect, useMemo, useState } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  hydrateBrowserNotifications,
  patchNotificationPreferences,
  saveEmailWhenReady,
} from 'Pods/ThreeDStudio/Store/actions/notificationPreferences'

function browserSupportsWebPush() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
}

const pushCleverPushCommand = (command) => {
  window.CleverPush = window.CleverPush || []
  window.CleverPush.push(command)
}

// Shared state for the CGTrader AI notification toggles (first-generation popover and Settings
// modal). The email toggle mirrors User#email_settings and is written straight through to the API.
// The browser toggle persists to localStorage and defers to the CleverPush SDK opt-in flow, only
// persisting once the SDK confirms a subscription, so the stored flag never claims push permission
// the browser did not grant.
export default function useNotificationPreferences() {
  const pageConfig = usePageConfig()
  const cleverPushEnabled = Boolean(pageConfig?.cleverPushEnabled)
  const currentUserId = pageConfig?.currentUserId
  const serverEmailWhenReady = pageConfig?.aiStudioEmailNotifications
  const storedPreferences = useThreeDStudioStore(state => state.notificationPreferences)
  const [isBrowserPushSupported, setIsBrowserPushSupported] = useState(false)

  useEffect(() => {
    hydrateBrowserNotifications()
  }, [])

  useEffect(() => {
    setIsBrowserPushSupported(cleverPushEnabled && browserSupportsWebPush())
  }, [cleverPushEnabled])

  // Re-tag users who opted in before the user_id attribute was wired up, and
  // re-tag the subscription when the browser switches accounts -- otherwise
  // Cleverpush::Notifications::SendRunSucceeded keeps targeting the previous
  // account. The CleverPush queue ignores `setAttribute` for anonymous sessions,
  // so this is safe to push unconditionally once the SDK initialises (an
  // opted-out device has no subscription left to tag). The effect re-runs when
  // `currentUserId` becomes available so late-arriving page config (e.g. via the
  // `window-config-loaded` event) is honored.
  useEffect(() => {
    if (!cleverPushEnabled || !currentUserId) return

    pushCleverPushCommand(['setAttribute', 'user_id', String(currentUserId)])
  }, [cleverPushEnabled, currentUserId])

  // A session choice (non-null) always beats the server value, so a surface that mounts late --
  // NotificationsTab only mounts when its tab is selected -- cannot re-seed a stale page config
  // value over what the user just picked. Falls back to subscribed, matching the server default.
  const preferences = useMemo(() => ({
    ...storedPreferences,
    emailWhenReady: storedPreferences.emailWhenReady ?? serverEmailWhenReady ?? true,
  }), [storedPreferences, serverEmailWhenReady])

  const setEmailWhenReady = useCallback((enabled) => {
    saveEmailWhenReady(enabled)
  }, [])

  const setBrowserNotifications = useCallback((enabled) => {
    if (!enabled) {
      // The run-succeeded worker targets subscriptions by their user_id attribute,
      // so an opt-out has to clear that attribute and drop the subscription itself;
      // persisting the flag alone leaves the notifications arriving. Order matters:
      // the attribute can only be cleared while the subscription still exists.
      if (cleverPushEnabled) {
        pushCleverPushCommand(['setAttribute', 'user_id', ''])
        pushCleverPushCommand(['unsubscribe'])
      }
      patchNotificationPreferences({ browserNotifications: false })
      return
    }

    pushCleverPushCommand(['triggerOptIn', (err, subscriptionId) => {
      if (err || !subscriptionId) return
      patchNotificationPreferences({ browserNotifications: true })
    }])
    if (currentUserId) {
      pushCleverPushCommand(['setAttribute', 'user_id', String(currentUserId)])
    }
  }, [cleverPushEnabled, currentUserId])

  return {
    preferences,
    isBrowserPushSupported,
    // Absent page config value means signed out: there is nowhere to save the preference, and a
    // signed-out visitor cannot generate anything to be notified about.
    isEmailPreferenceAvailable: serverEmailWhenReady !== undefined,
    setEmailWhenReady,
    setBrowserNotifications,
  }
}
